import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Upload, Wand2, FileText, Search } from "lucide-react";
import TechnicalSheetForm from "./TechnicalSheetForm";
import hosys from "@/api/api";
import { toast } from "sonner";

export default function TechnicalSheetModal({
  open,
  onClose,
  onSave,
  ingredientName = "",
}) {
  const [activeTab, setActiveTab] = useState("manual");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(null);
  const [searching, setSearching] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [searchQuery, setSearchQuery] = useState(ingredientName);
  const [errorMsg, setErrorMsg] = useState("");

  const handleManualCreate = () => {
    setFormData(null);
    setShowForm(true);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setErrorMsg("");
    try {
      const res = await hosys.functions.invoke("searchProductTechnicalSheet", {
        product_name: searchQuery,
      });
      if (res?.data?.found) {
        setFormData({
          ...res.data.data,
          source_link: res.data.source_link || "",
        });
        setShowForm(true);
      } else {
        toast.error("No s'ha pogut trobar informació sobre aquest producte");
        setErrorMsg("No s'ha pogut trobar informació sobre aquest producte");
      }
    } catch (error) {
      toast.error("Error en la cerca: " + (error.message || "error desconegut"));
      setErrorMsg("Error en la cerca: " + (error.message || "error desconegut"));
    } finally {
      setSearching(false);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    setErrorMsg("");
    try {
      // Upload file
      const uploadRes = await hosys.integrations.Core.UploadFile({ file });
      
      if (!uploadRes?.file_url) {
        throw new Error("No s'ha pogut pujar el fitxer");
      }
      
      // Extract data from file
      const res = await hosys.functions.invoke("extractTechnicalSheetFromFile", {
        file_url: uploadRes.file_url,
        product_name: ingredientName,
      });

      if (res?.data?.status === "success") {
        setFormData({
          ...res.data.data,
          source_file_url: uploadRes.file_url,
        });
        setShowForm(true);
        toast.success("Fitxer processat correctament");
      } else {
        // Even if extraction fails, show form with file URL
        setFormData({
          name: ingredientName,
          source_file_url: uploadRes.file_url,
        });
        setShowForm(true);
        toast.info("Fitxer pujat. Completa les dades manualment.");
      }
    } catch (error) {
      const msg = error.message || "error desconegut";
      toast.error("Error al pujar fitxer: " + msg);
      setErrorMsg("Error al pujar fitxer: " + msg);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSaveForm = (data) => {
    onSave({
      ...data,
      source_type: activeTab === "manual" ? "manual" : activeTab === "photo" ? "photo" : activeTab === "pdf" ? "pdf" : "internet_ai",
    });
    setShowForm(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Afegir fitxa tècnica - {ingredientName}</DialogTitle>
          </DialogHeader>

          {!showForm ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="manual">Manual</TabsTrigger>
                <TabsTrigger value="photo">Foto</TabsTrigger>
                <TabsTrigger value="pdf">PDF</TabsTrigger>
                <TabsTrigger value="internet_ai">Cercar</TabsTrigger>
              </TabsList>

              <TabsContent value="manual" className="space-y-4 mt-4">
                <p className="text-sm text-gray-600">Crea una fitxa tècnica manualment amb tots els detalls del producte.</p>
                <Button onClick={handleManualCreate} className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F]">
                  <FileText className="w-4 h-4 mr-2" /> Crear fitxa manualment
                </Button>
              </TabsContent>

              <TabsContent value="photo" className="space-y-4 mt-4">
                <p className="text-sm text-gray-600">Puja una foto de l'etiqueta del producte. El sistema intentarà extreure la informació.</p>
                <label className="border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-[#7B2D3F] transition-colors text-center">
                  <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium">Clica per seleccionar una foto</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, "photo")}
                    className="hidden"
                    disabled={uploadingFile}
                  />
                </label>
              </TabsContent>

              <TabsContent value="pdf" className="space-y-4 mt-4">
                <p className="text-sm text-gray-600">Puja un PDF amb la fitxa tècnica del producte.</p>
                <label className="border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-[#7B2D3F] transition-colors text-center">
                  <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium">Clica per seleccionar un PDF</p>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => handleFileUpload(e, "pdf")}
                    className="hidden"
                    disabled={uploadingFile}
                  />
                </label>
              </TabsContent>

              <TabsContent value="internet_ai" className="space-y-4 mt-4">
                <p className="text-sm text-gray-600">Cerca informació pública sobre el producte. La informació es marcarà com a pendent revisió.</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Nom del producte..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <Button
                    onClick={handleSearch}
                    disabled={searching || !searchQuery.trim()}
                    className="bg-[#7B2D3F] hover:bg-[#5B1D2F]"
                  >
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <TechnicalSheetForm
              open={true}
              onClose={() => setShowForm(false)}
              onSave={handleSaveForm}
              initialData={formData}
              isLoading={uploadingFile || searching}
            />
          )}

          {(uploadingFile || searching) && (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="w-5 h-5 animate-spin text-[#7B2D3F] mr-2" />
              <span className="text-sm">Processant...</span>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {showForm && formData && (
        <TechnicalSheetForm
          open={showForm}
          onClose={() => setShowForm(false)}
          onSave={handleSaveForm}
          initialData={formData}
        />
      )}
    </>
  );
}